import Datetime from 'utils/datetime/datetime'

/** A setting the backend named as conflicting, reduced to what a message needs. */
export type SettingConflict = {
  /** The setting's "Reason" in the UI, falling back to its name when blank. */
  label: string
  startTime: ISODateTime | null
  endTime: ISODateTime | null
}

/**
 * What a setting-save response actually said, independent of React and translation.
 *
 * The refusal arrives as 200 with a null payload, so this is decided in `onSuccess`
 * rather than from an HTTP status. `unrecognized` exists because the previous code
 * folded it into the overlap branch and reported overlap as the cause of a response
 * it had not identified — a guess presented as a diagnosis.
 */
export type SettingSaveOutcome =
  | { kind: 'saved' }
  | { kind: 'overlap'; conflicts: SettingConflict[] }
  | { kind: 'unrecognized' }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function trimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function isoDateTimeOrNull(value: unknown): ISODateTime | null {
  return typeof value === 'string' ? (value as ISODateTime) : null
}

function toConflict(setting: Record<string, unknown>): SettingConflict {
  const comment = trimmedString(setting.comment)
  return {
    label: comment !== '' ? comment : trimmedString(setting.name),
    startTime: isoDateTimeOrNull(setting.start_time),
    endTime: isoDateTimeOrNull(setting.end_time),
  }
}

/** Conflicts from one field, ignoring anything that is not a usable row. */
function toConflicts(value: unknown): SettingConflict[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter(isRecord).map(toConflict)
}

/**
 * Renders a conflict as `'reason' (2026-08-19 19:00 – 2026-08-20 01:00)`.
 *
 * The date is included rather than the time alone because these windows routinely cross
 * midnight — the case in the aurora-e2e failure this came from.
 */
export function formatSettingConflict(conflict: SettingConflict): string {
  const window = [conflict.startTime, conflict.endTime]
    .map((time) => (time ? Datetime.toLocalTime(time, 'longDay') : null))
    .filter((time): time is string => time !== null)
    .join(' – ')

  return window === '' ? `'${conflict.label}'` : `'${conflict.label}' (${window})`
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * The alert for a save the backend refused: the conflicts it named, or an admission that the
 * response was not recognized. A refusal that named no conflict once produced no alert at all,
 * so the setting simply did not appear and nothing said why.
 */
export function getSettingSaveRefusalMessage(
  outcome: Exclude<SettingSaveOutcome, { kind: 'saved' }>,
  failureMessage: string,
  t: Translate
): string {
  if (outcome.kind === 'overlap') {
    return `${failureMessage} ${t('It overlaps {{conflicts}}.', {
      conflicts: outcome.conflicts.map(formatSettingConflict).join(', '),
    })}`
  }
  return `${failureMessage} ${t('The server returned a response we did not recognize.')}`
}

/**
 * Classifies a single setting-save response.
 *
 * Takes `unknown` deliberately. `apiClient<T>` asserts its type parameter rather than
 * validating the JSON, so the declared contract is a claim about the backend, not a
 * guarantee about the value. This function's whole purpose is to turn an unrecognized
 * payload into an alert, so it must not itself throw on one — `in` against a primitive and
 * spreading a non-array both would, and the caller is a mutation `onSuccess` where that
 * surfaces as no alert at all.
 */
export function classifySettingSaveResult(result: unknown): SettingSaveOutcome {
  if (!isRecord(result)) {
    return { kind: 'unrecognized' }
  }

  // Discriminated on the required arrays, not on `time_series` / `created`. Those two are
  // optional, so a refusal that omits the key entirely would fall through both branches and
  // be reported as unrecognized while naming conflicts it had in hand.
  if ('overlapping_time_series_settings' in result) {
    if (result.time_series) {
      return { kind: 'saved' }
    }
    const conflicts = [
      ...toConflicts(result.overlapping_time_series_settings),
      ...toConflicts(result.overlapping_settings),
    ]
    return conflicts.length > 0 ? { kind: 'overlap', conflicts } : { kind: 'unrecognized' }
  }

  if ('overlapping' in result) {
    if (result.created) {
      return { kind: 'saved' }
    }
    const conflicts = toConflicts(result.overlapping)
    return conflicts.length > 0 ? { kind: 'overlap', conflicts } : { kind: 'unrecognized' }
  }

  return { kind: 'unrecognized' }
}

/**
 * Classifies a batch of setting-save responses as one outcome.
 *
 * A batch is saved only if every response was. Otherwise the conflicts from every
 * refusal are reported together, since the user has to resolve all of them; a batch
 * with no identifiable conflict at all is unrecognized rather than blamed on overlap.
 */
export function classifySettingSaveResults(results: unknown): SettingSaveOutcome {
  if (!Array.isArray(results) || results.length === 0) {
    return { kind: 'unrecognized' }
  }

  const outcomes = results.map(classifySettingSaveResult)
  if (outcomes.every((outcome) => outcome.kind === 'saved')) {
    return { kind: 'saved' }
  }

  const conflicts = outcomes.flatMap((outcome) => (outcome.kind === 'overlap' ? outcome.conflicts : []))
  return conflicts.length > 0 ? { kind: 'overlap', conflicts } : { kind: 'unrecognized' }
}
